import * as React from 'react';
import PropTypes from 'prop-types';
import { useAutocomplete } from '@mui/base/AutocompleteUnstyled';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';
import Autocomplete, { autocompleteClasses } from '@mui/material/Autocomplete';
import { blueGrey, orange, red, teal } from '@mui/material/colors';
import { CloseOutlined, CloseRounded,  } from '@mui/icons-material';
import { color, rgbToHex } from '@mui/system';
import { TextField } from '@mui/material';

const Root = styled('div')(
  ({ theme }) => `
  xcolor: ${
    theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,.85)'
  };
  font-size: 14px;
`,
);

const Label = styled('label')`
  padding: 0 0 4px;
  line-height: 1.5;
  display: block;
`;

const InputWrapper = styled('div')(
  ({ theme }) => `
  xborder: 1px solid ${theme.palette.mode === 'dark' ? '#434343' : '#d9d9d9'};
  xbackground-color: ${theme.palette.mode === 'dark' ? '#141414' : '#fff'};
  border-radius: 4px;
  padding: 1px;
  display: flex;
  flex-wrap: wrap;

  &:hover {
    xborder-color: ${theme.palette.mode === 'dark' ? '#177ddc' : teal[400].toString()};
  }

  &.focused {
    xborder-color: ${theme.palette.mode === 'dark' ? '#177ddc' : teal[800].toString()};
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }

  & input {
    xbackground-color: ${theme.palette.mode === 'dark' ? '#141414' : '#fff'};
    xcolor: ${
      theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,.85)'
    };
    height: 30px;
    box-sizing: border-box;
    padding: 4px 6px;
    width: 0;
    min-width: 30px;
    flex-grow: 1;
    border: 0;
    margin: 0;
    outline: 0;
  }
`,
);

function Tag(props) {
  const { label, onDelete, ...other } = props;
  return (
    <div {...other}>
      <span>{label}</span>
      <CloseRounded onClick={onDelete} />
    </div>
  );
}

Tag.propTypes = {
  label: PropTypes.string.isRequired,
  onDelete: PropTypes.func.isRequired,
};

const StyledTag = styled(Tag)(
  ({ theme }) => `
  display: flex;
  align-items: center;
  height: 24px;
  margin: 2px;
  line-height: 22px;
  xfont-family: 'Montserrat, sans-serif';
  background-color: ${
    theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : blueGrey[100]
  };
  border: 1px solid ${theme.palette.mode === 'dark' ? '#303030' : '#e8e8e8'};
  border-radius: 2px;
  box-sizing: content-box;
  padding: 4px 4px 4px 10px;
  outline: 0;
  overflow: hidden;

  &:focus {
    xborder-color: ${theme.palette.mode === 'dark' ? '#177ddc' : '#40a9ff'};
    xbackground-color: ${theme.palette.mode === 'dark' ? '#003b57' : '#e6f7ff'};
  }

  & span {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  & svg {
    font-size: 16px;
    cursor: pointer;
    padding: 4px;
  }
`,
);

const Listbox = styled('ul')(
  ({ theme }) => `
  margin: 2px 0 0;
  padding: 0;
  position: absolute;
  list-style: none;
  background-color: ${theme.palette.mode === 'dark' ? '#141414' : '#fff'};
  overflow: auto;
  max-height: 250px;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 1;
  font-size: 1.2em;

  & li {
    padding: 6px 12px;
    display: flex;

    & span {
      flex-grow: 1;
    }

    & svg {
      color: transparent;
      margin-left: ${theme.spacing(4)};
    }
  }

  & li[aria-selected='true'] {
    background-color: ${theme.palette.mode === 'dark' ? '#2b2b2b' : '#fafafa'};
    font-weight: 600;

    & svg {
      color: ${teal[400]};
    }
  }

  & li.${autocompleteClasses.focused} {
    background-color: ${theme.palette.mode === 'dark' ? '#003b57' : orange[200]};
    cursor: pointer;

    & svg {
      color: currentColor;
    }
  }
`,
);


export default function VocabSelector({ fullWidth }) {

      return (
        <Autocomplete
        multiple
        id="tags-standard"
        options={VOCABS}
        getOptionLabel={(option) => option.title}
        defaultValue={[VOCABS[0]]}
        fullWidth={fullWidth}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="standard"
            label="Filter"
            placeholder='Vocabulary [TODO]'
            sx={{
                "&:before": { border: "none", },
            }}
          />
        )}
      />
      );
    }
    
    // Top 100 films as rated by IMDb users. http://www.imdb.com/chart/top
    const VOCABS = [
      { title: 'schema.org', year: 1994 },
      { title: 'w3id.org', year: 1972 },
      { title: "imsglobal.github.io", year: 313},
   
    ];